import React from "react";
import { Avatar, AvatarProps, Tooltip } from "@mui/material";

/* ──────────────────────────────────────────────────────────────────────
   StudentAvatar — single source of truth for rendering a student's avatar
   across the app. Replaces raw `<Avatar>` fallbacks that all rendered the
   same flat grey / faint-primary disc, making student lists impossible to
   scan at a glance.

   Behavior:
     • If the student has an uploaded image, render that image
       (objectFit: cover, perfect circle).
     • Otherwise render the student's initial on a DETERMINISTIC colour
       picked from a fixed accessible palette. The colour is derived from a
       stable key (student id → email → name), so the same student ALWAYS
       gets the same colour across reloads, pages, sorting and filtering —
       and it is never random per render.

   The colour helpers are exported so non-avatar surfaces (badges, borders)
   can reuse the exact same colour a student's avatar shows.
   ────────────────────────────────────────────────────────────────────── */

/** Minimal shape any student-like object should expose to StudentAvatar. */
export interface StudentLike {
  id?: number | string | null;
  image?: string | null;
  profile_image?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  email?: string | null;
}

/* Fixed, accessible palette. Every background is a 700-level shade that
   pairs with white text at >= 4.5:1 contrast (WCAG AA), so the initial
   stays readable in both light and dark mode. Order is intentional and
   must stay stable — changing it re-shuffles every student's colour. */
export const STUDENT_AVATAR_PALETTE = [
  "#1e40af", // dark blue
  "#0f766e", // teal
  "#15803d", // green
  "#7e22ce", // purple
  "#c2410c", // orange
  "#b91c1c", // red
  "#4338ca", // indigo
  "#0e7490", // cyan
  "#b45309", // amber
] as const;

/** Deterministic 32-bit string hash (djb2). Same input → same output. */
function hashString(input: string): number {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    // hash * 33 + c, kept in 32-bit space with | 0
    hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/** Resolve the best display name for the student. */
export function resolveStudentName(
  s: StudentLike | null | undefined,
  fallbackLabel?: string
): string {
  if (!s) return fallbackLabel ?? "";
  return (
    s.name?.trim() ||
    [s.first_name, s.last_name].filter(Boolean).join(" ").trim() ||
    fallbackLabel ||
    ""
  );
}

/**
 * The stable key a student's colour is derived from. Prefers the immutable
 * id; falls back to email, then name — never list position — so sorting or
 * filtering the list can never change a student's colour.
 */
export function studentColorKey(s: StudentLike | null | undefined): string {
  if (!s) return "";
  if (s.id !== null && s.id !== undefined && String(s.id).trim() !== "")
    return `id:${s.id}`;
  if (s.email && s.email.trim()) return `email:${s.email.trim().toLowerCase()}`;
  const name = resolveStudentName(s);
  return name ? `name:${name.toLowerCase()}` : "";
}

/** The palette colour deterministically assigned to a student. */
export function studentAvatarColor(s: StudentLike | null | undefined): string {
  const key = studentColorKey(s);
  if (!key) return STUDENT_AVATAR_PALETTE[0];
  return STUDENT_AVATAR_PALETTE[hashString(key) % STUDENT_AVATAR_PALETTE.length];
}

/** First readable initial for the fallback disc. */
function resolveInitial(name: string): string {
  const ch = name.trim().charAt(0);
  return ch ? ch.toUpperCase() : "?";
}

interface StudentAvatarProps extends Omit<AvatarProps, "src" | "children"> {
  student: StudentLike | null | undefined;
  /** Square edge length in pixels. Defaults to 36 (typical row avatar). */
  size?: number;
  /** Wrap in a Tooltip showing the student's resolved display name. */
  tooltip?: boolean;
  /** Override the displayed name (used by tooltip + alt attr + initial). */
  fallbackLabel?: string;
}

const StudentAvatar: React.FC<StudentAvatarProps> = ({
  student,
  size = 36,
  tooltip = false,
  fallbackLabel,
  sx,
  ...rest
}) => {
  const name = resolveStudentName(student, fallbackLabel);
  const initial = resolveInitial(name);
  const src = student?.image || student?.profile_image || "";
  const bg = studentAvatarColor(student);

  const avatar = (
    <Avatar
      src={src || undefined}
      alt={name}
      sx={{
        width: size,
        height: size,
        bgcolor: bg,
        color: "#fff",
        fontSize: Math.max(11, Math.round(size * 0.42)),
        fontWeight: 700,
        lineHeight: 1,
        // Keep the photo filling the circle without distortion.
        "& img": { objectFit: "cover" },
        // Caller sx wins last so per-call rings / borders still apply.
        ...(sx as object),
      }}
      {...rest}
    >
      {/* Children only render when src is missing/broken; MUI handles the swap. */}
      {!src && initial}
    </Avatar>
  );

  if (tooltip && name) {
    return (
      <Tooltip title={name} arrow>
        <span style={{ display: "inline-flex" }}>{avatar}</span>
      </Tooltip>
    );
  }
  return avatar;
};

export default StudentAvatar;
